<script setup>
import Faq from './Components/Faq.vue';
import Subfooter from './Includes/Subfooter.vue';
import { Head, useForm, router } from "@inertiajs/vue3";

import Input from './Components/Form/Input.vue';
import Select from './Components/Form/Select.vue';
import Textarea from './Components/Form/Textarea.vue';
import Checkbox from './Components/Form/Checkbox.vue';
import SaveButton from './Components/Form/SaveButton.vue';

import { ref, onMounted, watch } from "vue";
import Button from './Components/Button.vue';

const subjects = [
    { id: 'algemeen', label: 'Algemeen' },
    { id: 'verkoop', label: 'Verkoop' },
    { id: 'service', label: 'Service' },
    { id: 'proefrit', label: 'Proefrit' },
    { id: 'overig', label: 'Overig' },
]

const form = useForm({
    first_name: null,
    last_name: null,
    email: null,
    phone_number: null,
    subject: "algemeen",
    message: null,
    contact: null,
    terms: false,
})

const saveButton = ref(null);

const contactFormSendSuccess = ref(false);
const contactFormSendError = ref(false);

function submit() {
    saveButton.value.setSaving(true);

    form.post('/contact', {
        preserveScroll: true,
        onSuccess: () => {
            contactFormSendSuccess.value = true;
            if (saveButton.value) {
                saveButton.value.setSaving(false);
            }
        },
        onError: () => {
            if (!form.hasErrors) {
                contactFormSendError.value = true;
            }

            if (saveButton.value) {
                saveButton.value.setSaving(false);
            }
        }
    });
}

const params = new URLSearchParams(window.location.search);
const subject = params.get('subject')?.toLowerCase();
if (subject) {
    form.subject = subject;
    //create a template message for asking for a proefrit
    const vehicle = params.get('vehicle');
    if (subject === 'proefrit' && vehicle) {
        form.message = `Beste Zwagers Automotive, \n\nIk zou graag een proefrit willen maken in de ${vehicle}. \n\nMet vriendelijke groet, \n\n`;
    }
}
</script>
<template>

    <Head>
        <title>Over Zwagers Automotive - Specialist in Betrouwbare Gebruikte Auto's</title>
        <meta name="description" content="Service & onderhoud bij Zwagers Automotive: vakkundige APK-keuringen, kleine en grote beurten, en betrouwbaar auto-onderhoud. Ontdek onze persoonlijke service en zorg dat jouw auto in topconditie blijft." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Services APK & Onderhoud - Zwagers Automotive" />
        <meta property="og:description" content="Service & onderhoud bij Zwagers Automotive: vakkundige APK-keuringen, kleine en grote beurten, en betrouwbaar auto-onderhoud. Ontdek onze persoonlijke service en zorg dat jouw auto in topconditie blijft." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/img/showroom/zwagers_showroom_35.jpg" />
        <!-- Pas de URL aan naar een afbeelding van het team of de showroom -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Services APK & Onderhoud - Zwagers Automotive" />
        <meta name="twitter:description" content="Service & onderhoud bij Zwagers Automotive: vakkundige APK-keuringen, kleine en grote beurten, en betrouwbaar auto-onderhoud. Ontdek onze persoonlijke service en zorg dat jouw auto in topconditie blijft." />
        <meta name="twitter:image" content="/img/showroom/zwagers_showroom_35.jpg" />
        <!-- Pas de URL aan naar een geschikte afbeelding -->
    </Head>

    <section class="flex flex-col bg-black lg:-space-y-56 w-full overflow-hidden">
        <!--hero-->
        <div id="hero" class="h-full text-white">
            <div class="relative lg:h-screen lg:min-h-[768px] max-h-[1200px] flex flex-col">
                <video :src="'/videos/zwagers_automotive_hero.webm'" autoplay loop muted playsinline class="opacity-60 absolute w-full h-full aspect-video object-cover" />
                <!-- <img :src="'/img/showroom/zwagers_showroom_35.jpg'" class="opacity-60 absolute w-full h-full aspect-video object-cover" /> -->
                <div class="z-20 flex justify-center">
                    <div class="w-11/12 px:0 md:px-6 xl:px-0 lg:h-5/6 max-w-6xl lg:min-h-[768px] py-24 lg:py-0 lg:pt-24">
                        <div class="flex flex-col gap-10 h-full justify-center">
                            <h1 class="text-shadow w-full md:w-9/12 xl:w-8/12">Service &amp; Onderhoud</h1>
                            <p class="text-shadow hidden sm:block w-full md:w-9/12 xl:w-8/12">
                                Bij ons kun je rekenen op vakkundige service en betrouwbaar onderhoud voor jouw auto. 
                                Of het nu gaat om een kleine beurt, grote onderhoudsbeurt, APK of reparatie: wij zorgen dat je veilig en zorgeloos de weg op kunt. 
                                Onze ervaren monteurs werken met moderne apparatuur en originele onderdelen, zodat jouw auto in topconditie blijft.
                            </p>
                            <div class="flex sm:flex-row flex-col gap-6">
                                <Button href="https://wa.me/+31617328160" type="href" target="_blank"
                                    class="primary-button" :useInertia="false">
                                    <font-awesome-icon :icon="['fab', 'whatsapp']" class="h-5 w-5" />
                                    Whatsapp
                                </Button>
                                <Button href="#contactform" type="href" class="secondary-white-button">
                                    Contact
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="absolute h-24 bg-gradient-to-b from-black/0 from-35% to-black bottom-0 left-0 right-0 w-full"></div>
            </div>
        </div>
        <div id="sub-hero" class="flex justify-center py-8 lg:py-28 z-20 bg-gradient-to-b from-transparent via-black via-35% to-black text-white">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 flex flex-col gap-10 max-w-6xl">
                <div class="text-shadow w-full md:w-9/12 xl:w-8/12">
                    <p>Onderhoud &amp; APK</p>
                    <h1 class="text-balance">Wij brengen jouw auto in topconditie</h1>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-20">
                    <div
                        class="flex flex-col gap-4 bg-white rounded-xl shadow-lg p-6 text-black scale-100 hover:scale-105 transition duration-500 group">
                        <font-awesome-icon
                            class="w-8 h-8 md:w-12 md:h-12 mb-4 text-timber transition duration-500"
                            :icon="['fas', 'car-wrench']" />
                        <h6
                            class="font-semibold text-lg md:text-2xl group-hover:text-timber transition duration-500">
                            Kleine beurt</h6>
                        <p class="text-sm md:text-base text-pretty">
                            Periodieke controle en verversing van olie en filters. Zo blijft je auto betrouwbaar en veilig rijden.
                        </p>
                    </div>
                    <div
                        class="flex flex-col gap-4 bg-white rounded-xl shadow-lg p-6 text-black scale-100 hover:scale-105 transition duration-500 group">
                        <font-awesome-icon
                            class="w-8 h-8 md:w-12 md:h-12 mb-4 text-timber transition duration-500"
                            :icon="['fas', 'garage-open']" />
                        <h6
                            class="font-semibold text-lg md:text-2xl group-hover:text-timber transition duration-500">
                            Grote beurt</h6>
                        <p class="text-sm md:text-base text-pretty">
                            Uitgebreide inspectie, vervanging van essentiële onderdelen en controle van alle vitale systemen.
                        </p>
                    </div>
                    <div
                        class="flex flex-col gap-4 bg-white rounded-xl shadow-lg p-6 text-black scale-100 hover:scale-105 transition duration-500 group">
                        <font-awesome-icon
                            class="w-8 h-8 md:w-12 md:h-12 mb-4 text-timber transition duration-500"
                            :icon="['fas', 'car-battery']" />
                        <h6
                            class="font-semibold text-lg md:text-2xl group-hover:text-timber transition duration-500">
                            Veiligheids check</h6>
                        <p class="text-sm md:text-base text-pretty">
                            Controle van accu, remmen en vloeistoffen voor optimale veiligheid en prestaties.
                        </p>
                    </div>
                    <div
                        class="flex flex-col gap-4 bg-white rounded-xl shadow-lg p-6 text-black scale-100 hover:scale-105 transition duration-500 group">
                        <font-awesome-icon
                            class="w-8 h-8 md:w-12 md:h-12 mb-4 text-timber transition duration-500"
                            :icon="['fas', 'carpool']" />
                        <h6
                            class="font-semibold text-lg md:text-2xl group-hover:text-timber transition duration-500">
                            APK Keuring</h6>
                        <p class="text-sm md:text-base text-pretty">
                            Ook voor je jaarlijkse APK ben je bij ons aan het juiste adres. Snel, vakkundig en betrouwbaar geregeld.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <main class="text-white">
        <section id="onderhoud" class="bg-white text-black flex justify-center pt-16 md:pt-32 lg:pt-40">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl space-y-12 lg:inline-flex lg:space-y-0 lg:space-x-20">
                <div class="flex flex-col gap-6 lg:h-full lg:justify-center">
                    <h2 class="font-bold">Vakkundig onderhoud</h2>
                    <p>
                        Goed onderhoud is essentieel voor de levensduur en veiligheid van je auto. <br><br>
                        Wij voeren onderhoud uit volgens fabrieksspecificaties, 
                        met gebruik van hoogwaardige onderdelen en moderne apparatuur. Of het nu gaat om een kleine of grote beurt, 
                        een APK-keuring of een specifieke reparatie: wij zorgen dat jouw auto in topconditie blijft.  <br><br>
                        Transparantie, betrouwbaarheid en persoonlijke service staan bij ons voorop. Jouw specialist in diagnoses en storingen opzoeken. Zo kun jij zorgeloos de weg op.
                    </p>
                </div>
                <div class="h-auto flex lg:min-w-[500px] flex-row justify-center lg:justify-normal">
                    <div class="rounded-lg flex items-center justify-center align-middle group p-1 aspect-square w-96">
                        <img :src="'/img/showroom/zwagers_automotive_apk_en_onderhoud_5.webp'" alt="Monteur voert onderhoud uit aan auto bij Zwagers Automotive" class="h-full w-auto aspect-square object-cover rounded-lg text-white" />
                    </div>
                </div>
            </div>
        </section>
        <section id="apk" class="bg-white text-black flex justify-center py-16 md:py-32 lg:py-40">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl space-y-12 lg:inline-flex lg:space-y-0 lg:space-x-20">
                <div class="h-auto flex lg:min-w-[500px] flex-row justify-center lg:justify-normal">
                    <div class="rounded-lg flex items-center justify-center align-middle group p-1 aspect-square w-96 ml-auto">
                        <img :src="'/img/showroom/zwagers_automotive_apk_en_onderhoud_1.webp'" alt="APK keuring Borne - Grondige controle van jouw auto bij Zwagers Automotive" class="h-full w-auto aspect-square object-cover rounded-lg text-white" />
                    </div>
                </div>
                <div class="flex flex-col gap-6 lg:h-full lg:justify-center">
                    <h2 class="font-bold">APK-keuring meester</h2>
                    <p>
                        De Algemene Periodieke Keuring (APK) is wettelijk verplicht en zorgt ervoor dat jouw auto veilig en milieuvriendelijk blijft rijden. <br><br>
                        Wij voeren de APK keuring snel en vakkundig uit volgens de laatste eisen. 
                        Onze monteurs controleren alle belangrijke punten, zoals remmen, verlichting, banden en uitstoot. 
                        Zo weet je zeker dat je veilig de weg op kunt en voorkom je onverwachte verrassingen. <br><br>
                        Combineer je APK met onderhoud? Dan profiteer je van extra voordeel!
                    </p>
                </div>
            </div>
        </section>
        <Faq id="lease" />
        <section id="contactform" class="flex flex-col items-center text-black py-10 md:py-20 lg:py-28 gap-28">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl">
                <div class="flex flex-col md:grid grid-cols-3 w-full justify-between gap-8">
                    <div class="flex justify-center sm:justify-center md:justify-start">
                        <Button href="tel:+31617328160" type="href"
                            class="group text-black hover:text-tanhover flex flex-col items-center"
                            :useInertia="false">
                            <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                                <font-awesome-icon class="h-8" :icon="['far', 'phone']" />
                                <span
                                    class="text-center text-3xl font-semibold tracking-tight lg:text-4xl md:items-center">Telefoon</span>
                            </div>
                            <span class="text-center text-base/7 text-gray-400">+31(0)617328160</span>
                        </Button>
                    </div>
                    <div class="flex justify-center sm:justify-center">
                        <Button href="mailto:info@zwagersautomotive.nl" type="href"
                            class="group text-black hover:text-tanhover flex flex-col items-center"
                            :useInertia="false">
                            <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                                <font-awesome-icon class="h-8" :icon="['far', 'envelope']" />
                                <span
                                    class="text-center text-3xl font-semibold tracking-tight lg:text-4xl items-center">E-mail</span>
                            </div>
                            <span class="text-center text-base/7 text-gray-400">info@zwagersautomotive.nl</span>
                        </Button>
                    </div>
                    <div class="flex justify-center sm:justify-center md:justify-end">
                        <Button href="https://maps.app.goo.gl/JmCJRMgToeovnWfb7" target="_blank" type="href"
                            class="group text-black hover:text-tanhover flex flex-col items-center"
                            :useInertia="false">
                            <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                                <font-awesome-icon class="h-8" :icon="['far', 'car-building']" />
                                <span
                                    class="text-center text-3xl font-semibold tracking-tight lg:text-4xl items-center">Locatie</span>
                            </div>
                            <span class="text-center text-base/7 text-gray-400">Ambachtstraat 3F, 7622 AS Borne</span>
                        </Button>
                    </div>
                </div>
                <form @submit.prevent="submit" class="mt-10 md:mt-20 relative">
                    <div class="mt-4 grid sm:grid-cols-12 text-sm font-normal text-gray-900 gap-5">
                        <div class="col-span-full sm:col-span-6">
                            <Input v-model="form.first_name" label="Voornaam" placeholder="Vul je voornaam in" required
                                :error="form.errors.first_name" inputId="firstName" />
                        </div>
                        <div class="col-span-full sm:col-span-6">
                            <Input v-model="form.last_name" label="Achternaam" placeholder="Vul je achternaam in"
                                required :error="form.errors.last_name" inputId="lastName" />
                        </div>
                        <div class="col-span-full sm:col-span-6">
                            <Input v-model="form.email" label="E-mailadres" placeholder="Vul je e-mailadres in"
                                type="email" required :error="form.errors.email" inputId="email" />
                        </div>
                        <div class="col-span-full sm:col-span-6">
                            <Input v-model="form.phone_number" label="Telefoonnummer" type="tel"
                                placeholder="Vul je telefoonnummer in" :error="form.errors.phone_number"
                                inputId="phoneNumber" />
                        </div>
                        <div class="col-span-full">
                            <Select v-model="form.subject" label="Onderwerp" :options="subjects" valueName="label"
                                valueId="id" :error="form.errors.subject" inputId="subject" />
                        </div>
                        <div class="col-span-full">
                            <Textarea v-model="form.message" label="Bericht" required :error="form.errors.message"
                                inputId="message" />
                        </div>
                        <div class="w-full flex flex-row flex-wrap justify-between col-span-full gap-y-2">
                            <Checkbox v-model="form.terms" :error="form.errors.terms"
                                label="Ik ga akkoord met de voorwaarden" inputId="terms" />
                            <SaveButton class="primary-button" text="Bericht versturen"
                                savingText="Bericht aan het versturen..." ref="saveButton" />
                        </div>
                    </div>
                    <div v-if="contactFormSendSuccess || contactFormSendError"
                        class="absolute bg-black w-full h-full top-0 flex items-center justify-center rounded-lg text-white p-4">
                        <p v-if="contactFormSendSuccess">Je bericht is succesvol verzonden!</p>
                        <p v-if="contactFormSendError">Er is een fout opgetreden bij het verzenden van je bericht.
                            Controleer de invoer en probeer het opnieuw.</p>
                    </div>
                </form>
            </div>
        </section>
    </main>
    <Subfooter />
</template>
